<script lang="ts">
	import { cn } from "#lib/web-ui/ui/utils.js";
	import type { WithElementRef } from "bits-ui";
	import type { HTMLAttributes } from "svelte/elements";
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import type { LucideIcon } from "@lucide/svelte";
	import ChartBarIcon from "@lucide/svelte/icons/chart-bar";
	import ExternalLinkIcon from "@lucide/svelte/icons/external-link";
	import XIcon from "@lucide/svelte/icons/x";

	interface Props {
		icon?: LucideIcon;
		title: string;
		subtitle: string;
		buttonText: string;
		buttonHref: string;
	}

	let {
		icon = ChartBarIcon,
		title = "Engaging Title!",
		subtitle = "This title should get you to visit google",
		buttonText = "Visit Google",
		buttonHref = "https://google.com",
		ref = $bindable(null),
		class: className,
		...restProps
	}: Props & WithElementRef<HTMLAttributes<HTMLDivElement>> = $props();

	let visibleFloating = $state(true);
	let visibleDesktop = $state(true);

	const Icon = icon;
</script>

<!-- Desktop banner - only visible on md screens and up -->
{#if visibleDesktop}
	<div class={cn("relative z-10 mx-auto hidden max-w-5xl md:block", className)} {...restProps}>
		<div class="-mt-12"></div>
		<div class="border-primary overflow-hidden rounded border-2 shadow-lg">
			<div class="bg-foreground relative px-10 py-8">
				<button
					onclick={() => (visibleDesktop = false)}
					class="text-background/60 hover:text-background absolute top-3 right-3 focus:outline-hidden"
					aria-label="Close"
				>
					<XIcon class="h-4 w-4" />
				</button>

				<div class="flex items-center justify-between gap-6">
					<div class="flex items-center gap-4">
						<div class="bg-primary shrink-0 rounded-full p-3">
							<Icon class="text-primary-foreground h-6 w-6" />
						</div>
						<div class="text-left">
							<h3 class="text-background text-xl font-bold">{title}</h3>
							<p class="text-background/70 mt-1">
								{subtitle}
							</p>
						</div>
					</div>
					<Button href={buttonHref} variant="outline" class="min-w-52">
						{buttonText}
						<ExternalLinkIcon
							class="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1"
						/>
					</Button>
				</div>
			</div>
		</div>
	</div>
{/if}

<!-- Mobile floating banner - only visible on smaller screens -->
{#if visibleFloating}
	<div
		class="fixed right-0 bottom-0 left-0 z-50 mx-auto mb-4 max-w-2xl transform transition-all duration-300 ease-in-out md:hidden"
	>
		<div class="border-primary mx-4 overflow-hidden rounded border-2 shadow-xl">
			<div class="bg-foreground relative px-4 py-3">
				<button
					onclick={() => (visibleFloating = false)}
					class="text-background/60 hover:text-background absolute top-2 right-2 focus:outline-hidden"
					aria-label="Close"
				>
					<XIcon class="h-4 w-4" />
				</button>

				<div class="flex items-center justify-between">
					<div class="flex items-center gap-3">
						<div class="bg-primary shrink-0 rounded-full p-2">
							<Icon class="text-primary-foreground h-4 w-4" />
						</div>
						<div>
							<span class="text-background text-sm font-medium">{title}</span>
							<span class="text-background/70 ml-2 hidden text-xs sm:inline">
								{subtitle}
							</span>
						</div>
					</div>
					<Button href={buttonHref} variant="outline" size="sm" class="mr-4 h-8 text-xs">
						{buttonText}
						<ExternalLinkIcon class="ml-1 h-3 w-3" />
					</Button>
				</div>
			</div>
		</div>
	</div>
{/if}
